/**
 * @fileoverview Preserve current banking edits, confirmation, and delegation while the server disables guided verification. BankingDetails selects this view from the server-owned workflow flag; backend services retain action authorization.
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { AppButton, AppIcon } from '../shared';
import './BankingDetails.css';

/** Display full banking details immediately and record explicit staff actions. */
export default function ExistingBankingDetails({ reviewId, result, matterNumber, clientId, onUpdated, onReload }) {
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(false);
  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const [employees, setEmployees] = useState(null);
  const [delegate, setDelegate] = useState('');
  const generation = useRef(0);
  const operation = useRef(null);
  const context = matterNumber && clientId ? { matter_number: matterNumber, client_id: Number(clientId) } : {};
  const destination = result.submission.destination;
  const status = result.banking;

  /** Load full numbers without requiring a separate form or confirmation. */
  async function load() {
    const current = generation.current;
    setLoading(true); setError(''); setBank(null);
    try {
      const { data } = await api.post(`/payment-instructions/${encodeURIComponent(reviewId)}/reveal`, {
        revision: result.revision, ...context,
      });
      if (generation.current === current) setBank(data.destination);
    } catch (failure) {
      if (generation.current === current) setError(failure.response?.data?.error || 'Bank details could not be loaded.');
    } finally { if (generation.current === current) setLoading(false); }
  }
  useEffect(() => {
    generation.current += 1;
    if (result.can_reveal) load();
    return () => { generation.current += 1; };
    // A saved revision is already returned with its matching full bank details.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, matterNumber, clientId]);

  /** Save a staff edit or confirm the exact version currently displayed. */
  async function act(action, method) {
    const current = generation.current;
    const payload = { action, revision: result.revision, banking_revision: bank.staffRevision || 0,
      ...context, ...(action === 'save' ? { routing_number: routing, account_number: account } : { method }) };
    const key = JSON.stringify(payload);
    if (operation.current?.key !== key) operation.current = { key, id: crypto.randomUUID() };
    setBusy(true); setError(''); setNotice('');
    try {
      const { data } = await api.put(`/payment-instructions/${encodeURIComponent(reviewId)}/banking`, {
        ...payload, operation_id: operation.current.id,
      });
      if (generation.current !== current) return;
      setBank(data.destination); setEditing(false); operation.current = null;
      setNotice(action === 'save' ? 'Changes saved' : 'Banking details verified');
      onUpdated(data);
    } catch (failure) {
      if (generation.current === current) setError(failure.response?.data?.error || 'Banking details could not be saved.');
    } finally { if (generation.current === current) setBusy(false); }
  }

  /** Copy an identifier while preserving its leading zeros. */
  async function copy(value, label) {
    try { await navigator.clipboard.writeText(value); setNotice(`${label} copied`); }
    catch { setError('Copying was blocked. Select the number to copy it.'); }
  }

  /** Offer every active employee as an optional verifier. */
  async function openDelegation() {
    setBusy(true); setError('');
    const current = generation.current;
    try {
      const { data } = await api.get(`/payment-instructions/${encodeURIComponent(reviewId)}/delegate`);
      if (generation.current === current) setEmployees(data.employees);
    } catch { if (generation.current === current) setError('Verifiers could not be loaded.'); }
    finally { if (generation.current === current) setBusy(false); }
  }

  /** Create or reassign the delegated verification task. */
  async function assign() {
    setBusy(true); setError(''); const current = generation.current;
    try {
      const { data } = await api.post(`/payment-instructions/${encodeURIComponent(reviewId)}/delegate`, {
        employee_id: Number(delegate), revision: result.revision,
      });
      if (generation.current !== current) return;
      onUpdated(data); setEmployees(null); setNotice('Verification delegated');
    } catch (failure) {
      if (generation.current === current) setError(failure.response?.data?.error || 'Verification could not be delegated.');
    } finally { if (generation.current === current) setBusy(false); }
  }

  const visible = result.can_reveal && bank;
  const confirmed = status?.confirmed && bank && (status.banking_revision ?? 0) === (bank.staffRevision || 0)
    && (!status.version_id || status.version_id === bank.staffVersionId);
  return <section className="banking-details" aria-label="Banking details">
    <header className="banking-details__header">
      <h3><AppIcon icon="pi pi-wallet" aria-hidden="true" /> Banking details</h3>
      {confirmed ? <span className="banking-details__verified"><AppIcon icon="pi pi-check-circle" aria-hidden="true" />
        {status.method === 'stripe' ? 'Verified · Stripe only' : status.method === 'video' ? 'Video verified' : 'Phone verified'}</span>
        : result.can_reveal ? <span className="banking-details__pending"><AppIcon icon="pi pi-clock" aria-hidden="true" /> Awaiting staff acceptance</span> : null}
    </header>
    <p className="banking-details__bank">{destination.bank_name} · {destination.account_type}</p>
    <p>Account holder: {destination.account_holder}</p>
    {loading ? <p role="status">Loading bank details…</p> : null}
    {visible ? <>
      <div className="banking-details__numbers">
        <label>Routing number
          <span className="banking-details__number">
            {editing ? <input aria-label="Routing number" inputMode="numeric" autoComplete="off" value={routing}
              maxLength={9} onChange={event => setRouting(event.target.value)} disabled={busy} /> : <strong>{bank.routingNumber}</strong>}
            {!editing ? <button type="button" title="Copy routing number" aria-label="Copy routing number" onClick={() => copy(bank.routingNumber, 'Routing number')}><AppIcon icon="pi pi-copy" aria-hidden="true" /></button> : null}
          </span>
        </label>
        <label>Account number
          <span className="banking-details__number">
            {editing ? <input aria-label="Account number" inputMode="numeric" autoComplete="off" value={account}
              maxLength={17} onChange={event => setAccount(event.target.value)} disabled={busy} /> : <strong>{bank.accountNumber}</strong>}
            {!editing ? <button type="button" title="Copy account number" aria-label="Copy account number" onClick={() => copy(bank.accountNumber, 'Account number')}><AppIcon icon="pi pi-copy" aria-hidden="true" /></button> : null}
          </span>
        </label>
      </div>
      <div className="banking-details__actions">
        {editing ? <>
          <AppButton disabled={busy || !/^[0-9]{9}$/.test(routing) || !/^[0-9]{4,17}$/.test(account)} icon="pi pi-check" onClick={() => act('save')}>Save changes</AppButton>
          <AppButton variant="secondary" disabled={busy} onClick={() => setEditing(false)}>Cancel</AppButton>
        </> : <>
          <AppButton variant="secondary" icon="pi pi-pencil" disabled={busy} onClick={() => { setRouting(bank.routingNumber); setAccount(bank.accountNumber); setEditing(true); setNotice(''); }}>Edit details</AppButton>
          {!confirmed ? <>
            <AppButton icon="pi pi-phone" disabled={busy} onClick={() => act('confirm', 'phone')}>Confirm by phone</AppButton>
            <AppButton variant="secondary" icon="pi pi-camera" disabled={busy} onClick={() => act('confirm', 'video')}>Confirm by video</AppButton>
          </> : null}
        </>}
      </div>
      {!editing && !confirmed ? <div className="banking-details__alternatives">
        <AppButton variant="secondary" icon="pi pi-user-plus" disabled={busy} onClick={openDelegation}>Delegate verification</AppButton>
        {result.can_use_stripe_only ? <AppButton variant="secondary" icon="pi pi-shield" disabled={busy} onClick={() => act('confirm', 'stripe')}>Use Stripe verification only</AppButton> : null}
      </div> : null}
    </> : !loading ? <p>Account ending {status?.account_last4 || destination.account_last4}</p> : null}
    {status?.recorded_by && (!status.confirmed || confirmed) ? <p className="banking-details__attribution">{status.confirmed ? 'Confirmed' : 'Updated'} by {status.recorded_by} · {new Date(status.recorded_at).toLocaleString()}</p> : null}
    {status?.delegate?.status === 'open' ? <p className="banking-details__attribution">Verification assigned to {status.delegate.name}</p> : null}
    {employees ? <div className="banking-details__delegate">
      <label>Verify with <select value={delegate} onChange={event => setDelegate(event.target.value)} disabled={busy}>
        <option value="">Choose employee</option>{employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
      </select></label>
      <AppButton disabled={busy || !delegate} onClick={assign}>Assign verification</AppButton>
      <AppButton variant="secondary" disabled={busy} onClick={() => setEmployees(null)}>Cancel</AppButton>
    </div> : null}
    {error ? <div role="alert"><p>{error}</p>{result.can_reveal ? <AppButton variant="secondary" icon="pi pi-refresh" onClick={onReload || load}>Reload details</AppButton> : null}</div> : null}
    {notice ? <p role="status">{notice}</p> : null}
  </section>;
}
